// Chakra imports
import {
  Box,
  Text,
  SimpleGrid,
  useColorModeValue,
  Icon,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Divider,
  Button,
  IconButton,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
// Custom components
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import Card from "components/card/Card";
import React, { useState, useEffect, useRef } from "react";
import {
  MdSettings,
  MdSecurity,
  MdInfo,
  MdCheckCircle,
  MdCancel,
  MdSchedule,
  MdPlayArrow,
  MdDelete,
  MdWarning,
} from "react-icons/md";
import { formatNumber, formatDate } from "utils/format";
import systemService from "services/systemService";

export default function SystemManagement() {
  // Chakra Color Mode - All hooks must be called at the top level
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'white');
  const cardBg = useColorModeValue('white', 'navy.800');
  const brandColor = useColorModeValue('brand.500', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const borderColor = useColorModeValue('secondaryGray.200', 'whiteAlpha.100');
  const badgeBg = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [triggeringJobs, setTriggeringJobs] = useState({});
  const [tuitionSettings, setTuitionSettings] = useState(null);
  
  // Tuition Settings Edit State
  const [tuitionFormData, setTuitionFormData] = useState({ phase_1: 0, phase_2: 0, phase_3: 0 });
  const [updatingTuition, setUpdatingTuition] = useState(false);
  const [tuitionFormErrors, setTuitionFormErrors] = useState({});
  
  // Clear Database State
  const [clearStep, setClearStep] = useState(1); // 1: First confirmation, 2: Credentials, 3: Second confirmation, 4: Third confirmation
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [validating, setValidating] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [validationError, setValidationError] = useState(null);
  // ...existing code...
  
  const toast = useToast();
  
  // Modals
  const { isOpen: isClearOpen, onOpen: onClearOpen, onClose: onClearClose } = useDisclosure();
  const { isOpen: isFinalConfirmOpen, onOpen: onFinalConfirmOpen, onClose: onFinalConfirmClose } = useDisclosure();
  const { isOpen: isTuitionEditOpen, onOpen: onTuitionEditOpen, onClose: onTuitionEditClose } = useDisclosure();
  // ...existing code...
  const cancelRef = useRef();

  // Fetch scheduler status and tuition settings
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch scheduler status
        const schedulerResult = await systemService.getSchedulerStatus();
        if (schedulerResult.success && schedulerResult.data) {
          setSchedulerStatus(schedulerResult.data);
        } else {
          setError(schedulerResult.error || 'Failed to load scheduler status');
        }

        // Fetch tuition settings
        const tuitionResult = await systemService.getTuitionSettings();
        if (tuitionResult.success && tuitionResult.data) {
          setTuitionSettings(tuitionResult.data);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load system data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle trigger job
  const handleTriggerJob = async (jobId) => {
    setTriggeringJobs(prev => ({ ...prev, [jobId]: true }));
    try {
      const result = await systemService.triggerJob(jobId);
      
      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Job triggered successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        // Refresh scheduler status after a short delay
        setTimeout(async () => {
          const refreshResult = await systemService.getSchedulerStatus();
          if (refreshResult.success && refreshResult.data) {
            setSchedulerStatus(refreshResult.data);
          }
        }, 1000);
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to trigger job',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Error triggering job:', err);
      toast({
        title: 'Error',
        description: 'Failed to trigger job. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setTriggeringJobs(prev => ({ ...prev, [jobId]: false }));
    }
  };

  // ...existing code...

  // Handle clear database - Step 1: First confirmation
  const handleClearDatabaseStart = () => {
    setClearStep(1);
    setCredentials({ email: '', password: '' });
    setValidationError(null);
    onClearOpen();
  };

  // Handle proceed to credentials step
  const handleProceedToCredentials = () => {
    setClearStep(2);
    setValidationError(null);
  };

  // Handle validate credentials
  const handleValidateCredentials = async () => {
    if (!credentials.email || !credentials.password) {
      setValidationError('Please enter both email and password');
      return;
    }

    setValidating(true);
    setValidationError(null);
    
    try {
      const result = await systemService.validateCredentials(credentials.email, credentials.password);
      
      if (result.success) {
        // Step 3: Second confirmation after successful validation
        setClearStep(3);
        setValidationError(null);
      } else {
        setValidationError(result.error || 'Invalid credentials');
      }
    } catch (err) {
      console.error('Error validating credentials:', err);
      setValidationError('Failed to validate credentials. Please try again.');
    } finally {
      setValidating(false);
    }
  };

  // Handle proceed to final confirmation
  const handleProceedToFinalConfirmation = () => {
    onClearClose();
    onFinalConfirmOpen();
  };

  // Handle final clear database
  const handleClearDatabase = async () => {
    setClearing(true);
    try {
      const result = await systemService.clearDatabase();

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Database cleared successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onFinalConfirmClose();
        // Reset state
        setClearStep(1);
        setCredentials({ email: '', password: '' });
        setValidationError(null);
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to clear database',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Error clearing database:', err);
      toast({
        title: 'Error',
        description: 'Failed to clear database. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setClearing(false);
    }
  };

  // Handle close clear database modal
  const handleCloseClearModal = () => {
    onClearClose();
    setClearStep(1);
    setCredentials({ email: '', password: '' });
    setValidationError(null);
  };

  // Handle open tuition settings edit modal
  const handleOpenTuitionEdit = () => {
    if (tuitionSettings) {
      setTuitionFormData({
        phase_1: tuitionSettings.phase_1 || 0,
        phase_2: tuitionSettings.phase_2 || 0,
        phase_3: tuitionSettings.phase_3 || 0,
      });
      setTuitionFormErrors({});
      onTuitionEditOpen();
    }
  };

  // Handle tuition settings form change
  const handleTuitionFormChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    setTuitionFormData(prev => ({ ...prev, [field]: numValue }));
    
    // Clear error for this field
    if (tuitionFormErrors[field]) {
      setTuitionFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle update tuition settings
  const handleUpdateTuitionSettings = async () => {
    // Validate
    const errors = {};
    if (tuitionFormData.phase_1 < 0) {
      errors.phase_1 = 'Phase 1 amount must be >= 0';
    }
    if (tuitionFormData.phase_2 < 0) {
      errors.phase_2 = 'Phase 2 amount must be >= 0';
    }
    if (tuitionFormData.phase_3 < 0) {
      errors.phase_3 = 'Phase 3 amount must be >= 0';
    }

    if (Object.keys(errors).length > 0) {
      setTuitionFormErrors(errors);
      return;
    }

    setUpdatingTuition(true);
    setTuitionFormErrors({});
    
    try {
      const result = await systemService.updateTuitionSettings(tuitionFormData);
      
      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Tuition settings updated successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        // Update local state
        setTuitionSettings(result.data);
        onTuitionEditClose();
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to update tuition settings',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Error updating tuition settings:', err);
      toast({
        title: 'Error',
        description: 'Failed to update tuition settings. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdatingTuition(false);
    }
  };

  if (loading) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
          <Spinner size="xl" color="brand.500" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Alert status="error" borderRadius="15px">
          <AlertIcon />
          <Text color={textColor}>{error}</Text>
        </Alert>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex direction='column' gap='20px'>
        <Flex justify='space-between' align='center' flexWrap='wrap' gap='15px' mb='10px'>
          <Text color={textColor} fontSize='2xl' fontWeight='700'>
            System Management
          </Text>
          <Button
            onClick={handleClearDatabaseStart}
            leftIcon={<Icon as={MdDelete} />}
            colorScheme='red'
            variant='solid'
            size='md'>
            Clear Database
          </Button>
        </Flex>

        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap='20px' mb='20px'>
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon 
                    w='32px' 
                    h='32px' 
                    as={schedulerStatus?.scheduler_running ? MdCheckCircle : MdCancel} 
                    color={schedulerStatus?.scheduler_running ? 'green.500' : 'red.500'} 
                  />
                }
              />
            }
            name='Scheduler Status'
            value={schedulerStatus?.scheduler_running ? 'Running' : 'Stopped'}
          />
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdSchedule} color={brandColor} />
                }
              />
            }
            name='Total Jobs'
            value={formatNumber(schedulerStatus?.total_jobs || 0)}
          />
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdSecurity} color='green.500' />
                }
              />
            }
            name='Security'
            value='Secure'
          />
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdInfo} color={brandColor} />
                }
              />
            }
            name='Last Checked'
            value={schedulerStatus?.checked_at ? formatDate(schedulerStatus.checked_at) : 'N/A'}
          />
        </SimpleGrid>

        {/* Scheduler Jobs Table */}
        {schedulerStatus && (
          <Card p='30px' bg={cardBg}>
            <Flex align='center' gap='10px' mb='20px'>
              <Icon as={MdSchedule} color={textColor} fontSize='xl' />
              <Text color={textColor} fontSize='xl' fontWeight='600'>
                Scheduled Jobs
              </Text>
            </Flex>
            <Divider borderColor={borderColor} mb='20px' />
            <Box overflowX='auto'>
              <Table variant='simple' color='secondaryGray.700'>
                <Thead>
                  <Tr>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Job Name
                    </Th>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Next Run Time
                    </Th>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Trigger
                    </Th>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Status
                    </Th>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Actions
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {schedulerStatus.jobs && schedulerStatus.jobs.length > 0 ? (
                    schedulerStatus.jobs.map((job, index) => (
                      <Tr key={job.id || index}>
                        <Td>
                          <Text color={textColor} fontSize='sm' fontWeight='600'>
                            {job.name || job.id}
                          </Text>
                          <Text color={textColorSecondary} fontSize='xs' fontFamily='mono'>
                            ID: {job.id}
                          </Text>
                        </Td>
                        <Td>
                          <Text color={textColor} fontSize='sm'>
                            {job.next_run_time ? formatDate(job.next_run_time) : 'N/A'}
                          </Text>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme='blue'
                            bg={badgeBg}
                            color={textColor}
                            fontSize='xs'
                            fontWeight='500'
                            px='8px'
                            py='4px'
                            borderRadius='8px'
                            fontFamily='mono'
                          >
                            {job.trigger || 'N/A'}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={job.pending ? 'orange' : 'green'}
                            bg={badgeBg}
                            color={textColor}
                            fontSize='xs'
                            fontWeight='500'
                            px='8px'
                            py='4px'
                            borderRadius='8px'
                          >
                            {job.pending ? 'PENDING' : 'ACTIVE'}
                          </Badge>
                        </Td>
                        <Td>
                          <IconButton
                            aria-label="Trigger job"
                            icon={<MdPlayArrow />}
                            size="sm"
                            colorScheme="blue"
                            variant="ghost"
                            onClick={() => handleTriggerJob(job.id)}
                            isLoading={triggeringJobs[job.id]}
                            loadingText="Triggering"
                          />
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan={5} textAlign='center' py='20px'>
                        <Text color={textColorSecondary} fontSize='sm'>
                          No jobs found
                        </Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          </Card>
        )}

        {/* Tuition Settings Card */}
        {tuitionSettings && (
          <Card p='30px' bg={cardBg}>
            <Flex align='center' justify='space-between' mb='20px'>
              <Flex align='center' gap='10px'>
                <Icon as={MdSettings} color={textColor} fontSize='xl' />
                <Text color={textColor} fontSize='xl' fontWeight='600'>
                  Tuition Settings
                </Text>
              </Flex>
              <Button
                size='sm'
                colorScheme='blue'
                variant='outline'
                onClick={handleOpenTuitionEdit}>
                Edit Settings
              </Button>
            </Flex>
            <Divider borderColor={borderColor} mb='20px' />
            <SimpleGrid columns={{ base: 1, md: 3 }} gap='20px'>
              <Box>
                <Text color={textColorSecondary} fontSize='sm' fontWeight='500' mb='5px'>
                  Phase 1
                </Text>
                <Text color={textColor} fontSize='lg' fontWeight='700'>
                  {formatNumber(tuitionSettings.phase_1)} FCFA
                </Text>
              </Box>
              <Box>
                <Text color={textColorSecondary} fontSize='sm' fontWeight='500' mb='5px'>
                  Phase 2
                </Text>
                <Text color={textColor} fontSize='lg' fontWeight='700'>
                  {formatNumber(tuitionSettings.phase_2)} FCFA
                </Text>
              </Box>
              <Box>
                <Text color={textColorSecondary} fontSize='sm' fontWeight='500' mb='5px'>
                  Phase 3
                </Text>
                <Text color={textColor} fontSize='lg' fontWeight='700'>
                  {formatNumber(tuitionSettings.phase_3)} FCFA
                </Text>
              </Box>
            </SimpleGrid>
            {tuitionSettings.updated_at && (
              <Text color={textColorSecondary} fontSize='xs' mt='15px' fontStyle='italic'>
                Last updated: {formatDate(tuitionSettings.updated_at)}
              </Text>
            )}
          </Card>
        )}
      </Flex>

      {/* Clear Database Modal - Multi-step confirmation */}
      <Modal isOpen={isClearOpen} onClose={handleCloseClearModal} size="xl" closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader color={textColor}>
            {clearStep === 1 && '⚠️ Clear Database - First Confirmation'}
            {clearStep === 2 && 'Clear Database - Verify Credentials'}
            {clearStep === 3 && '⚠️ Clear Database - Second Confirmation'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {clearStep === 1 && (
              <Flex direction="column" gap="20px">
                <Alert status="error" borderRadius="8px">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold" mb="5px">WARNING: This is a destructive operation!</Text>
                    <Text fontSize="sm">
                      This will permanently delete all data from:
                    </Text>
                    <Box as="ul" pl="20px" mt="10px" fontSize="sm">
                      <li>student_applications</li>
                      <li>tuitions</li>
                      <li>payments</li>
                      <li>blogs</li>
                      <li>categories</li>
                      <li>tutor_applications</li>
                      <li>monitoring</li>
                      <li>activity_logs</li>
                      <li>Any other collections except 'users'</li>
                    </Box>
                    <Text fontSize="sm" mt="10px" fontWeight="bold">
                      The users collection will NOT be affected.
                    </Text>
                    <Text fontSize="sm" mt="10px" color="red.600">
                      This operation cannot be undone!
                    </Text>
                  </Box>
                </Alert>
                <Text color={textColor} fontSize="sm">
                  Are you absolutely sure you want to proceed? You will need to verify your credentials in the next step.
                </Text>
              </Flex>
            )}

            {clearStep === 2 && (
              <Flex direction="column" gap="20px">
                <Alert status="warning" borderRadius="8px">
                  <AlertIcon />
                  <Text fontSize="sm">
                    Please enter your email and password to verify your identity before proceeding.
                  </Text>
                </Alert>
                <FormControl isRequired>
                  <FormLabel color={textColor}>Email</FormLabel>
                  <Input
                    type="email"
                    value={credentials.email}
                    onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                    placeholder="Enter your email"
                    bg={badgeBg}
                    color={textColor}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color={textColor}>Password</FormLabel>
                  <Input
                    type="password"
                    value={credentials.password}
                    onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                    placeholder="Enter your password"
                    bg={badgeBg}
                    color={textColor}
                  />
                </FormControl>
                {validationError && (
                  <Alert status="error" borderRadius="8px">
                    <AlertIcon />
                    <Text fontSize="sm">{validationError}</Text>
                  </Alert>
                )}
              </Flex>
            )}

            {clearStep === 3 && (
              <Flex direction="column" gap="20px">
                <Alert status="error" borderRadius="8px">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold" mb="5px">Second Confirmation Required</Text>
                    <Text fontSize="sm">
                      Your credentials have been validated. This is your second confirmation step.
                    </Text>
                    <Text fontSize="sm" mt="10px" fontWeight="bold">
                      You are about to permanently delete all data except users.
                    </Text>
                    <Text fontSize="sm" mt="10px" color="red.600">
                      This action cannot be undone!
                    </Text>
                  </Box>
                </Alert>
                <Text color={textColor} fontSize="sm">
                  Are you absolutely certain you want to proceed? One more confirmation will be required.
                </Text>
              </Flex>
            )}
          </ModalBody>
          <ModalFooter>
            {clearStep === 1 && (
              <>
                <Button variant="ghost" mr={3} onClick={handleCloseClearModal}>
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  onClick={handleProceedToCredentials}
                  leftIcon={<Icon as={MdWarning} />}>
                  I Understand, Continue
                </Button>
              </>
            )}
            {clearStep === 2 && (
              <>
                <Button variant="ghost" mr={3} onClick={handleCloseClearModal}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleValidateCredentials}
                  isLoading={validating}
                  loadingText="Validating">
                  Validate Credentials
                </Button>
              </>
            )}
            {clearStep === 3 && (
              <>
                <Button variant="ghost" mr={3} onClick={handleCloseClearModal}>
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  onClick={handleProceedToFinalConfirmation}
                  leftIcon={<Icon as={MdWarning} />}>
                  I Confirm, Proceed to Final Step
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Final Confirmation Dialog */}
      <AlertDialog
        isOpen={isFinalConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={onFinalConfirmClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={cardBg}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color={textColor}>
              <Flex align="center" gap="10px">
                <Icon as={MdWarning} color="red.500" fontSize="xl" />
                Final Confirmation - Clear Database
              </Flex>
            </AlertDialogHeader>
            <AlertDialogBody color={textColor}>
              <Alert status="error" borderRadius="8px" mb="15px">
                <AlertIcon />
                <Text fontSize="sm" fontWeight="bold">
                  This is your FINAL confirmation. This action will permanently delete all data except users and cannot be undone!
                </Text>
              </Alert>
              <Text fontSize="sm">
                Are you absolutely certain you want to proceed with clearing the database?
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onFinalConfirmClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleClearDatabase}
                ml={3}
                isLoading={clearing}
                loadingText="Clearing"
                leftIcon={<Icon as={MdDelete} />}>
                Yes, Clear Database
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Edit Tuition Settings Modal */}
      <Modal isOpen={isTuitionEditOpen} onClose={onTuitionEditClose} size="md">
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader color={textColor}>Edit Tuition Settings</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap="20px">
              <Alert status="info" borderRadius="8px">
                <AlertIcon />
                <Text fontSize="sm">
                  These amounts will be used when calculating total tuition amounts for students.
                </Text>
              </Alert>

              <FormControl isRequired isInvalid={!!tuitionFormErrors.phase_1}>
                <FormLabel color={textColor}>Phase 1 Amount (FCFA)</FormLabel>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={tuitionFormData.phase_1}
                  onChange={(e) => handleTuitionFormChange('phase_1', e.target.value)}
                  placeholder="Enter Phase 1 amount"
                  bg={badgeBg}
                  color={textColor}
                />
                {tuitionFormErrors.phase_1 && (
                  <Text color="red.500" fontSize="xs" mt="5px">
                    {tuitionFormErrors.phase_1}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired isInvalid={!!tuitionFormErrors.phase_2}>
                <FormLabel color={textColor}>Phase 2 Amount (FCFA)</FormLabel>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={tuitionFormData.phase_2}
                  onChange={(e) => handleTuitionFormChange('phase_2', e.target.value)}
                  placeholder="Enter Phase 2 amount"
                  bg={badgeBg}
                  color={textColor}
                />
                {tuitionFormErrors.phase_2 && (
                  <Text color="red.500" fontSize="xs" mt="5px">
                    {tuitionFormErrors.phase_2}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired isInvalid={!!tuitionFormErrors.phase_3}>
                <FormLabel color={textColor}>Phase 3 Amount (FCFA)</FormLabel>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={tuitionFormData.phase_3}
                  onChange={(e) => handleTuitionFormChange('phase_3', e.target.value)}
                  placeholder="Enter Phase 3 amount"
                  bg={badgeBg}
                  color={textColor}
                />
                {tuitionFormErrors.phase_3 && (
                  <Text color="red.500" fontSize="xs" mt="5px">
                    {tuitionFormErrors.phase_3}
                  </Text>
                )}
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onTuitionEditClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdateTuitionSettings}
              isLoading={updatingTuition}
              loadingText="Updating">
              Update Settings
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

